/* 徐盛（字文嚮） */
import { SG, K, gd, spMul, sigOf, landing, along } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, D, P, S } from '../define.ts';

export default defineHero({
  id: 'xusheng', name: '徐盛', sub: '字文嚮', faction: 'wu', role: '戰士', weapon: 'guandao',
  look: { weaponName: '破浪大刀', beard: 'bushy', bulk: 1.06, fx: '#6fc0ff', c: { plume: '#e9c46a', cape: '#6e1f1b' } },
  skills: [
    ['破陣斬', 'charge', '向前衝 300 並揮刀，沿途 {80/120/160/200} 傷害；命中武將時這招冷卻減半。', { x: { k: 'breakline', dmg: [80, 120, 160, 200], dist: 300, w: 80 }, aim: D(300, 80), cd: [12, 11, 10, 9], mp: [55, 60, 65, 70], el: null }],
    ['舟師執盾', 'shield', '舉盾 2 秒，前方傷害 -{40/50/60/70}%；結束時對前方衝擊 {60/90/120/150} 並擊退。', { x: { k: 'shieldwall', dr: [40, 50, 60, 70], dmg: [60, 90, 120, 150], dur: 2 }, aim: D(220, 160), cd: [16, 15, 14, 13], mp: 60, el: null }],
    ['迎風破浪', 'passive', '在河道中移速 +{5/8/10/13}%、攻擊 +{8/12/16/20}。', { pv: {}, sig: 'wave', ms: [5, 8, 10, 13], atk: [8, 12, 16, 20] }],
    ['疑城', 'summon', '在指定地點立起長 {500/600/700} 的假城牆 {6/7/8} 秒（擋不了路）：敵將第一次碰到城牆恐懼 {1/1.25/1.5} 秒；城牆 150 內的友軍對敵人隱形。', { x: { k: 'fakecity', len: [500, 600, 700], dur: [6, 7, 8], fear: [1, 1.25, 1.5] }, aim: P(700, 80), cd: [100, 90, 80], mp: [140, 160, 180], el: null, shout: '沿江疑城數百里，魏軍何足懼！' }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    breakline(u, a, X) {
      const from = [u.x, u.y], dest = landing(u.x, u.y, ...along(u.x, u.y, a.dir, X.dist) as [number, number]), sp = spMul(u);
      u.x = dest[0]; u.y = dest[1];
      const seg = [from[0], from[1], dest[0], dest[1]];
      let hero = false;
      for (const t of this.units) if (this.hostile(u, t) && !t.structure && SG.segDist(t.x, t.y, seg) <= X.w / 2 + t.r) {
        this.damage(u, t, X.dmg * sp, { stagger: 0.3 });
        if (t.kind === 'hero') hero = true;
      }
      if (hero) { const i = u.spec.skills.indexOf(a.sk); u.cd[i] *= 0.5; }
      this.fx({ k: 'slam', x: u.x, y: u.y, col: u.spec.fx });
    },
    shieldwall(u, a, X) {
      u.shield = { until: this.t + X.dur, dir: a.dir.slice(), dr: X.dr / 100 };
      const sp = spMul(u);
      this.later(X.dur, () => {
        if (!u.alive) return;
        const d = u.shield ? u.shield.dir : [u.f, 0];
        for (const t of this.enemiesNear(u, u.x, u.y, 230)) {
          const to = SG.dirTo(u, t.x, t.y);
          if (to[0] * d[0] + to[1] * d[1] < 0.3 || t.structure) continue;
          this.damage(u, t, X.dmg * sp, { stagger: 0.4 });
          if (t.alive) this.applyCC(u, t, { knockback: 90 });
        }
        this.fx({ k: 'slam', x: u.x + d[0] * 80, y: u.y + d[1] * 80 * K, col: u.spec.fx });
      });
    },
    fakecity(u, a, X) {
      const c = a.pt, d = a.dir, tx = -d[1], ty = d[0], h = X.len / 2;
      const seg = [c[0] - tx * h, c[1] - ty * h * K, c[0] + tx * h, c[1] + ty * h * K];
      this.addWall(seg, 30, X.dur, u.team, 'fake');
      this.fakeCities = (this.fakeCities || []).filter((f: { until: number }) => f.until > this.t);
      this.fakeCities.push({ seg, team: u.team, until: this.t + X.dur, fear: X.fear, scared: new Set(), owner: u });
      this.fx({ k: 'slam', x: c[0], y: c[1], col: '#d8a860' });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 舟師執盾：前方來的傷害減少
    hook('damage.before', 15, function (c) {
      const t = c.t, s = t.shield;
      if (!s || s.until <= this.t) return;
      const from = c.own, to = SG.dirTo(t, from.x, from.y);
      if (to[0] * s.dir[0] + to[1] * s.dir[1] > 0.2) c.a *= 1 - s.dr;
    }),
    // 疑城：敵將第一次碰到恐懼；城牆旁的友軍隱形
    hook('step.after', 33, function (dt) {
      const fc = this.fakeCities;
      if (!fc || !fc.length) return;
      this.fakeCities = fc.filter((f: { until: number }) => f.until > this.t);
      for (const f of this.fakeCities) for (const o of this.units) {
        if (!o.alive || o.structure || o.team > 1) continue;
        const dd = SG.segDist(o.x, o.y, f.seg);
        if (o.team === f.team) { if (dd <= 150) o.st.invis = Math.max(o.st.invis, 0.25); }
        else if (o.kind === 'hero' && dd <= 50 && !f.scared.has(o)) { f.scared.add(o); this.applyCC(f.owner, o, { fear: f.fear }); this.fx({ k: 'puff', x: o.x, y: o.y }); }
      }
    }),
    // 迎風破浪：在河道中加速、加攻
    hook('aura', 43, function () {
      for (const u of this.units) {
        const wv = u.alive && u.kind === 'hero' ? sigOf(u, 'wave') : null;
        if (wv && SG.inWater && SG.inWater(u.x, u.y)) this.addBuff(u, { id: 'wave', t: 0.7, ms: wv.v('ms') / 100, atk: wv.v('atk') });
      }
    }),
    hook('revive', 74, function (u) { u.shield = null; }),
  ],
  // 電腦什麼時候放這招
  ai: {
    breakline: ({ t, d }) => !!t && d < 320,
    shieldwall: ({ hero, d, hpR, foeHeroes }) => hero && d < 300 && (hpR < 0.7 || foeHeroes(400) >= 2),
    fakecity: ({ hero, d, hpR, foeHeroes }) => hero && d < 650 && (foeHeroes(800) >= 2 || hpR < 0.35),
  },
});
